/**
 * Authentication Guard Hooks
 * Provides hooks for route protection and authentication checks
 */

'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '../providers/AuthProvider';

/**
 * Hook for protecting private routes
 * Returns loading state and handles redirection for unauthenticated users
 */
export function usePrivateRoute(redirectTo: string = '/login') {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!isLoading) {
      setIsChecking(false);
      
      if (!isAuthenticated) {
        router.replace(redirectTo);
      }
    }
  }, [isAuthenticated, isLoading, router, redirectTo]);

  return {
    isAuthenticated,
    isLoading: isLoading || isChecking,
    canAccess: isAuthenticated && !isLoading,
  };
}

/**
 * Hook for protecting public routes
 * Returns loading state and handles redirection for authenticated users
 */
export function usePublicRoute(redirectTo: string = '/dashboard') {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!isLoading) {
      setIsChecking(false);
      
      if (isAuthenticated) {
        router.replace(redirectTo);
      }
    }
  }, [isAuthenticated, isLoading, router, redirectTo]);

  return {
    isAuthenticated,
    isLoading: isLoading || isChecking,
    canAccess: !isAuthenticated && !isLoading,
  };
}

/**
 * Hook for conditional authentication checks
 * Returns authentication state without any redirection
 */
export function useAuthState() {
  const { isAuthenticated, isLoading, user, error } = useAuth();

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    isReady: !isLoading,
  };
}

/**
 * Hook for authentication actions
 * Returns authentication methods without state
 */
export function useAuthActions() {
  const { login, logout, clearError } = useAuth();

  return {
    login,
    logout,
    clearError,
  };
}

/**
 * Hook for role-based access control
 * Note: This is a placeholder for future role-based functionality
 */
export function useRoleGuard(requiredRoles: string[] = []) {
  const { user, isAuthenticated, isLoading } = useAuth();

  const hasRequiredRole = () => {
    if (!isAuthenticated || !user) return false;
    console.info('useRoleGuard requiredRoles', requiredRoles);
    // For now, all authenticated users have access
    // In the future implement role checking here
    // Example: return user.roles?.some(role => requiredRoles.includes(role));
    return true;
  };

  return {
    hasAccess: hasRequiredRole(),
    isLoading,
    isAuthenticated,
    user,
  };
}

/**
 * Hook for permission-based access control
 * Note: This is a placeholder for future permission-based functionality
 */
export function usePermissionGuard(requiredPermissions: string[] = []) {
  const { user, isAuthenticated, isLoading } = useAuth();

  const hasRequiredPermission = () => {
    if (!isAuthenticated || !user) return false;
    console.info('usePermissionGuard RequiredPermissions', requiredPermissions);
    // For now, all authenticated users have access
    // In the future implement permission checking here
    // Example: return user.permissions?.some(permission => requiredPermissions.includes(permission));
    return true;
  };

  return {
    hasAccess: hasRequiredPermission(),
    isLoading,
    isAuthenticated,
    user,
  };
}

/**
 * Hook for authentication error handling
 */
export function useAuthError() {
  const { error, clearError } = useAuth();

  const handleError = (callback?: (error: string) => void) => {
    if (error && callback) {
      callback(error);
    }
  };

  return {
    error,
    hasError: Boolean(error),
    clearError,
    handleError,
  };
}

/**
 * Hook for authentication loading states
 */
export function useAuthLoading() {
  const { isLoading } = useAuth();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  useEffect(() => {
    if (!isLoading) {
      setIsInitialLoading(false);
    }
  }, [isLoading]);

  return {
    isLoading,
    isInitialLoading,
    isReady: !isLoading && !isInitialLoading,
  };
}
